import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { HF, localChainName } from "./homeFeedUi";
import ProductTileImage from "./ProductTileImage";
import { ArrowIcon } from "./ClearanceView";
import { CLEARANCE_PATH } from "../../config/publicDeals";
import type { PublicPreviewDeal } from "../../services/PublicPreviewService";
import { bandBadge, publicDealPreviewQuery, publicFreeDealQuery } from "../../services/publicClearanceQueries";
import { DAILY_SPOTLIGHT_SHOWN_KEY, localDayKey, shouldShowDailySpotlight, spotlightChainsForDay } from "../../utils/dailySpotlight";
import { getProductImageUrl } from "../../utils/imageUtils";
import { track } from "../../utils/analytics";

/** Lets the home page settle before this asks for attention. */
const SHOW_DELAY_MS = 2500;
/** Chains tried before giving up for the day; each is a public call against a budget of twelve per ten minutes. */
const MAX_CHAINS_TRIED = 2;

type Spotlight = { chain: string; totalDeals: number; deals: PublicPreviewDeal[] };

async function loadSpotlight(queryClient: QueryClient, zip: string, today: string): Promise<Spotlight | null> {
  for (const chain of spotlightChainsForDay(today).slice(0, MAX_CHAINS_TRIED)) {
    const count = await queryClient.fetchQuery(publicFreeDealQuery(zip, chain)).catch(() => null);
    if (!count || count.totalDeals <= 0) continue;
    // Photos only for the chain we show; the count alone still makes the pitch.
    const preview = await queryClient.fetchQuery(publicDealPreviewQuery(zip, chain)).catch(() => null);
    const deals = (preview?.deals || []).filter((deal) => deal.retailer === chain && deal.image);
    return { chain, totalDeals: count.totalDeals, deals };
  }
  return null;
}

function readShownDay(): string | null {
  try {
    return window.localStorage.getItem(DAILY_SPOTLIGHT_SHOWN_KEY);
  } catch {
    // Storage blocked: treat as already shown so it never nags on every page view.
    return localDayKey();
  }
}

/**
 * Once a day on the home page, guests and free accounts get one nearby chain's
 * clearance count over a fan of its finds, with the way to see them all (sign up,
 * or upgrade on a free account). No ZIP yet: the same card asks for one.
 * `blocked` holds it back while another prompt (the join offer) is open.
 */
export default function ClearanceSpotlight({
  zip,
  onSubmitZip,
  unlockUrl,
  onUnlock,
  blocked,
  onOpenChange,
}: {
  zip: string;
  onSubmitZip: (zip: string) => void;
  unlockUrl: string;
  onUnlock: (trigger: string) => void;
  blocked: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [today] = useState(() => localDayKey());
  const [due] = useState(() => shouldShowDailySpotlight(readShownDay(), today));
  const [delayDone, setDelayDone] = useState(false);
  const [open, setOpen] = useState(false);
  const [zipInput, setZipInput] = useState("");
  const [zipError, setZipError] = useState("");
  const hasZip = /^\d{5}$/.test(zip);

  useEffect(() => {
    const timer = window.setTimeout(() => setDelayDone(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const spotlight = useQuery({
    queryKey: ["daily-clearance-spotlight", zip, today],
    queryFn: () => loadSpotlight(queryClient, zip, today),
    enabled: due && hasZip,
    staleTime: 30 * 60_000,
    retry: 0,
  });
  // With a ZIP, only a real count is worth a popup; without one, the card asks for the ZIP.
  const ready = hasZip ? Boolean(spotlight.data) : true;

  useEffect(() => {
    if (open || !due || !delayDone || !ready || blocked) return;
    setOpen(true);
    onOpenChange(true);
    try {
      window.localStorage.setItem(DAILY_SPOTLIGHT_SHOWN_KEY, today);
    } catch {
      // Private mode: may show again on the next visit.
    }
    track("daily_clearance_spotlight_shown", { surface: "web", retailer: spotlight.data?.chain ?? "local", total_deals: spotlight.data?.totalDeals ?? 0, has_zip: hasZip });
    // Opens once; later changes to these values never reopen it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [due, delayDone, ready, blocked]);

  const close = () => {
    setOpen(false);
    onOpenChange(false);
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const submitZip = (event: FormEvent) => {
    event.preventDefault();
    const value = zipInput.trim();
    if (!/^\d{5}$/.test(value)) {
      setZipError("Enter a 5-digit ZIP code");
      return;
    }
    setZipError("");
    onSubmitZip(value);
    track("guest_zip_entered", { surface: "web", retailer: "local", trigger: "daily_spotlight", source: "home_feed" });
  };

  const data = spotlight.data;
  const store = data ? localChainName(data.chain) : "";
  const deepest = data?.deals[0]?.discountBand;
  const fan = (data?.deals || []).slice(0, 3);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-2.5 sm:items-center" role="presentation">
      <div aria-hidden="true" onClick={close} className="absolute inset-0" style={{ background: "var(--hf-overlay)" }} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="clearance-spotlight-title"
        className="relative flex w-full max-w-[420px] flex-col items-center rounded-[34px] border px-6 pb-3 pt-8 text-center"
        style={{ background: HF.surface, borderColor: HF.border, color: HF.ink }}
        data-testid="clearance-spotlight"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-[var(--hf-hover-strong)]"
          style={{ background: HF.surface2, color: HF.muted }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>

        {data ? (
          <>
            {fan.length > 0 ? (
              <Link
                to={CLEARANCE_PATH}
                onClick={() => {
                  track("daily_clearance_spotlight_tapped", { surface: "web", retailer: data.chain, action: "preview" });
                  close();
                }}
                aria-label={`See ${store} clearance near you`}
                className="relative mt-1 block h-[150px] w-[290px] max-w-full"
              >
                {fan[1] && <FanCard deal={fan[1]} position="left" />}
                {fan[2] && <FanCard deal={fan[2]} position="right" />}
                <FanCard deal={fan[0]} position="front" />
              </Link>
            ) : (
              <PinBadge />
            )}
            <p className="m-0 mt-[22px] text-[12px] font-bold tracking-[1.2px]" style={{ color: HF.muted }}>
              TODAY AT {store.toUpperCase()} · {zip}
            </p>
            <h2 id="clearance-spotlight-title" className="m-0 mt-2 text-[32px] font-extrabold leading-[36px] tracking-[-1.1px] tabular-nums">
              {data.totalDeals.toLocaleString()} clearance {data.totalDeals === 1 ? "deal" : "deals"}
            </h2>
            <p className="m-0 mt-2 text-[15px] leading-5" style={{ color: HF.muted }}>
              {deepest ? `In store only · up to ${bandBadge(deepest)} off` : "In store only, not online"}
            </p>
            <Link
              to={unlockUrl}
              onClick={() => {
                track("daily_clearance_spotlight_tapped", { surface: "web", retailer: data.chain, action: "unlock" });
                onUnlock("daily_spotlight");
                close();
              }}
              className="mt-[26px] flex h-[54px] w-full items-center justify-center rounded-full text-[17px] font-bold no-underline hover:opacity-90"
              style={{ background: HF.inkBtn, color: HF.onInkBtn }}
              data-testid="clearance-spotlight-unlock"
            >
              See all deals
            </Link>
          </>
        ) : (
          <>
            <PinBadge />
            <h2 id="clearance-spotlight-title" className="m-0 mt-[18px] text-[26px] font-extrabold leading-[31px] tracking-[-0.7px]">
              What’s on clearance near you?
            </h2>
            <p className="m-0 mt-2 text-[15px] leading-5" style={{ color: HF.muted }}>
              {hasZip ? (spotlight.isFetching ? "Checking stores near you…" : "Nothing marked down nearby yet today.") : "In-store markdowns, updated daily."}
            </p>
            {!hasZip && (
              <form onSubmit={submitZip} className="mt-6 w-full">
                <label className="flex h-[58px] items-center rounded-full border pl-[22px] pr-1.5 focus-within:ring-2 focus-within:ring-[#FFD24A]" style={{ borderColor: HF.border, background: HF.surface2 }}>
                  <span className="sr-only">ZIP code</span>
                  <input
                    value={zipInput}
                    onChange={(event) => {
                      setZipInput(event.target.value.replace(/\D/g, "").slice(0, 5));
                      if (zipError) setZipError("");
                    }}
                    inputMode="numeric"
                    autoComplete="postal-code"
                    placeholder="ZIP code"
                    maxLength={5}
                    autoFocus
                    className="min-w-0 flex-1 border-0 bg-transparent text-[18px] font-semibold tracking-[1px] outline-none placeholder:font-normal placeholder:tracking-normal placeholder:text-[var(--hf-placeholder)]"
                    style={{ color: HF.ink }}
                  />
                  <button type="submit" aria-label="Show deals" className="flex h-[46px] w-[46px] flex-shrink-0 items-center justify-center rounded-full hover:opacity-90" style={{ background: HF.inkBtn, color: HF.onInkBtn }}>
                    <ArrowIcon />
                  </button>
                </label>
                {zipError && <p className="m-0 mt-2 text-[13px]" style={{ color: HF.red }} role="alert">{zipError}</p>}
              </form>
            )}
          </>
        )}

        <button type="button" onClick={close} className="mt-1 flex h-11 w-full items-center justify-center text-[15px] font-semibold" style={{ color: HF.muted }}>
          Not now
        </button>
      </div>
    </div>
  );
}

function FanCard({ deal, position }: { deal: PublicPreviewDeal; position: "left" | "right" | "front" }) {
  const front = position === "front";
  const place = front
    ? "left-[80px] top-0 h-[130px] w-[130px] rounded-[30px] bg-white"
    : `${position === "left" ? "left-1.5 -rotate-[9deg]" : "right-1.5 rotate-[9deg]"} top-[22px] h-[108px] w-[108px] rounded-[26px]`;
  return (
    <span className={`absolute flex items-center justify-center overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.45)] ${place}`} style={front ? undefined : { background: HF.tile }}>
      <ProductTileImage src={getProductImageUrl(deal.image, "300")} className="h-[74%] w-[74%] object-contain" />
      <span
        className={`absolute flex items-center rounded-full font-extrabold ${front ? "bottom-3 left-3 h-6 px-[9px] text-[13px]" : "bottom-2.5 left-2.5 h-[22px] px-2 text-[12px]"}`}
        style={{ background: "#0B0B0D", color: "#FF6B72" }}
      >
        {bandBadge(deal.discountBand)}
      </span>
    </span>
  );
}

function PinBadge() {
  return (
    <span className="flex h-16 w-16 items-center justify-center rounded-full" style={{ background: "#FFD24A" }} aria-hidden="true">
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#0B0B0D" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></svg>
    </span>
  );
}
